import type { ApproachDefinition } from 'types/approach';
import type { OutcomeCardHue } from '../../components/atoms/marketplace/OutcomeCardAtom';

/**
 * One derived outcome card produced by `deriveOutcomes()`.
 *
 * Strings are emitted as i18n keys + interpolation params so the consuming
 * molecule can resolve them via `t()`. The shape intentionally differs from
 * `OutcomeCardAtomProps` (which expects already-localized strings).
 */
export interface OutcomeCard {
  icon: string;
  hue: OutcomeCardHue;
  titleKey: string;
  titleParams?: Record<string, string | number>;
  bodyKey: string;
  bodyParams?: Record<string, string | number>;
}

const MAX_CARDS = 3;

const OUTCOME_KEY_PREFIX = 'marketplace.approachDetail.overview.outcomes';

/**
 * Derive 1–3 outcome cards from an approach's structural shape.
 *
 * Heuristics, in priority order:
 *  1. Arc cadence — "You'll work through {N} stages"
 *  2. Orbit cadence — "You'll get {N} scheduled check-ins"
 *  3. Provided resources — "You'll create a {schema}"
 *  4. Template-for — "You'll spin up a new {schema} sphere"
 *
 * Returns 0–3 cards; never throws on partial data.
 */
export function deriveOutcomes(approach: ApproachDefinition): OutcomeCard[] {
  if (!approach) return [];

  const cards: OutcomeCard[] = [];

  const arcCard = deriveArcOutcome(approach);
  if (arcCard) cards.push(arcCard);

  if (cards.length < MAX_CARDS) {
    const orbitCard = deriveOrbitOutcome(approach);
    if (orbitCard) cards.push(orbitCard);
  }

  if (cards.length < MAX_CARDS) {
    const resourceCard = deriveProvidedResourceOutcome(approach);
    if (resourceCard) cards.push(resourceCard);
  }

  if (cards.length < MAX_CARDS) {
    const templateCard = deriveTemplateForOutcome(approach);
    if (templateCard) cards.push(templateCard);
  }

  return cards.slice(0, MAX_CARDS);
}

function deriveArcOutcome(approach: ApproachDefinition): OutcomeCard | null {
  const arc = approach.arc;
  if (!arc) return null;

  const stageCount = arc.inlineDefinition?.stages?.length ?? 0;
  if (stageCount > 0) {
    return {
      icon: 'pi-sync',
      hue: 'green',
      titleKey: `${OUTCOME_KEY_PREFIX}.arcCadenceTitle`,
      titleParams: { count: stageCount },
      bodyKey: `${OUTCOME_KEY_PREFIX}.arcCadenceBody`,
      bodyParams: { count: stageCount },
    };
  }

  if (arc.templateSystemName) {
    return {
      icon: 'pi-sync',
      hue: 'green',
      titleKey: `${OUTCOME_KEY_PREFIX}.arcReferenceTitle`,
      bodyKey: `${OUTCOME_KEY_PREFIX}.arcReferenceBody`,
    };
  }

  return null;
}

function deriveOrbitOutcome(approach: ApproachDefinition): OutcomeCard | null {
  const orbit = approach.orbit;
  if (!orbit) return null;

  const inlineSchedules = orbit.inlineDefinition?.schedules;
  const scheduleCount = inlineSchedules ? Object.keys(inlineSchedules).length : 0;

  if (scheduleCount > 0) {
    return {
      icon: 'pi-calendar',
      hue: 'blue',
      titleKey: `${OUTCOME_KEY_PREFIX}.orbitCadenceTitle`,
      titleParams: { count: scheduleCount },
      bodyKey: `${OUTCOME_KEY_PREFIX}.orbitCadenceBody`,
      bodyParams: { count: scheduleCount },
    };
  }

  if (orbit.templateSystemName || orbit.source === 'template') {
    return {
      icon: 'pi-calendar',
      hue: 'blue',
      titleKey: `${OUTCOME_KEY_PREFIX}.orbitReferenceTitle`,
      bodyKey: `${OUTCOME_KEY_PREFIX}.orbitReferenceBody`,
    };
  }

  return null;
}

function deriveProvidedResourceOutcome(
  approach: ApproachDefinition,
): OutcomeCard | null {
  const resources = approach.providedResources;
  if (!resources) return null;

  const entries = Object.entries(resources);
  if (entries.length === 0) return null;

  const [, firstResource] = entries[0];
  const schema = firstResource?.resourceType ?? '';

  return {
    icon: 'pi-box',
    hue: 'amber',
    titleKey: `${OUTCOME_KEY_PREFIX}.providedResourceTitle`,
    titleParams: { schema },
    bodyKey: `${OUTCOME_KEY_PREFIX}.providedResourceBody`,
    bodyParams: { schema, count: entries.length },
  };
}

function deriveTemplateForOutcome(
  approach: ApproachDefinition,
): OutcomeCard | null {
  const templateFor = approach.templateFor;
  if (!templateFor) return null;

  return {
    icon: 'pi-globe',
    hue: 'violet',
    titleKey: `${OUTCOME_KEY_PREFIX}.templateForTitle`,
    titleParams: { schema: templateFor },
    bodyKey: `${OUTCOME_KEY_PREFIX}.templateForBody`,
    bodyParams: { schema: templateFor },
  };
}
